import React, { useEffect, useState, useMemo } from "react";
import { useParams, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { getWithTokenAPI, deleteWithTokenAPI, patchWithTokenAPI } from "../../util/Api";
import { batch_get_all, students_list_autoapproved, students_list_approved } from "../../util/Apiendpoits";
import { SafeToast } from "../Toast/ShowToast";
import "../../styles/batchdetails.css";
import {
  FiArrowLeft,
  FiBookOpen,
  FiUsers,
  FiDollarSign,
  FiFileText,
  FiCheckCircle,
  FiClock,
  FiEye,
  FiExternalLink,
  FiSearch,
  FiTrash2,
  FiX,
  FiEdit2,
} from "react-icons/fi";
import TableSkeleton from "../ReusableComponent/TableSkeleton";

const BatchDetails = () => {
  const { batchId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryInstId = searchParams.get("institutionId");
  const { token, userId } = useSelector((state) => state.auth);

  const [activeTab, setActiveTab] = useState("tests"); // "tests" | "students"
  const [loading, setLoading] = useState(true);
  const [batchInfo, setBatchInfo] = useState(location.state?.batch || null);
  const passedInstId =
    queryInstId ||
    location.state?.institutionId ||
    batchInfo?.Institution_id?._id ||
    batchInfo?.Institution_id;
  const targetInstitutionId = passedInstId || userId;
  const [tests, setTests] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [testSearch, setTestSearch] = useState("");
  const [studentSearch, setStudentSearch] = useState("");
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [showDeleteBatchModal, setShowDeleteBatchModal] = useState(false);
  const [isDeletingBatch, setIsDeletingBatch] = useState(false);
  const [publishingTestId, setPublishingTestId] = useState(null);

  const handlePublishTest = async (test) => {
    if (!test?._id) {
      SafeToast.error("Test identifier not found");
      return;
    }

    try {
      setPublishingTestId(test._id);
      const res = await patchWithTokenAPI(token, `/batch/${test._id}/publish`, {
        isPublished: true,
      });

      if (res?.statusCode === 200 || !res?.error) {
        SafeToast.success(`Test ${test.testNo || ""} published successfully!`);
        fetchBatchDetails();
      } else {
        SafeToast.error(res?.message || "Failed to publish test");
      }
    } catch (err) {
      console.error("Error publishing test:", err);
      SafeToast.error("Failed to publish test");
    } finally {
      setPublishingTestId(null);
    }
  };

  const handleEditTest = (test) => {
    navigate("/upload", {
      state: {
        editBatchName: batchInfo?.testBatchName || test.testBatchName,
        editExamType: test.examType || batchInfo?.examType,
        editTestNo: test.testNo,
        editTestId: test._id,
      },
    });
  };

  const handleDeleteBatch = async () => {
    try {
      setIsDeletingBatch(true);
      const batchName = batchInfo?.testBatchName;
      if (!batchName) {
        SafeToast.error("Batch details not found");
        return;
      }

      const instId = userId || "695f3f832929fa82230aaf33";
      const testsRes = await getWithTokenAPI(
        token,
        `/batch/get-all?Institution_id=${instId}&testBatchName=${encodeURIComponent(batchName)}&all=true`
      );

      const batchIdsToDelete = new Set();
      if (testsRes?.statusCode === 200 && Array.isArray(testsRes.data?.data)) {
        testsRes.data.data.forEach((b) => {
          if (b?._id) batchIdsToDelete.add(b._id);
        });
      }
      tests.forEach((t) => {
        if (t?._id) batchIdsToDelete.add(t._id);
      });
      if (batchId) batchIdsToDelete.add(batchId);

      if (batchIdsToDelete.size === 0) {
        SafeToast.error("No tests found to delete for this batch");
        return;
      }

      const deletePromises = Array.from(batchIdsToDelete).map((id) =>
        deleteWithTokenAPI(token, `/batch/delete/${id}`)
      );
      await Promise.all(deletePromises);

      SafeToast.success("Test batch and associated tests deleted permanently");
      setShowDeleteBatchModal(false);
      navigate(queryInstId ? `/batches?institutionId=${queryInstId}` : "/batches");
    } catch (err) {
      console.error("Error deleting batch:", err);
      SafeToast.error("Failed to delete test batch");
    } finally {
      setIsDeletingBatch(false);
    }
  };

  // Fetch all tests and enrolled students for this batch
  const fetchBatchDetails = async () => {
    try {
      setLoading(true);
      const instId = targetInstitutionId || "695f3f832929fa82230aaf33";

      // 1. Fetch batch information if not present from state
      let currentBatch = batchInfo;
      if (!currentBatch || !currentBatch.testBatchName) {
        const batchRes = await getWithTokenAPI(
          token,
          `${batch_get_all}${instId}&all=true`
        );
        if (batchRes?.statusCode === 200) {
          const allBatches = batchRes.data.data || [];
          currentBatch = allBatches.find((b) => b._id === batchId || b.id === batchId);
          if (currentBatch) {
            setBatchInfo(currentBatch);
          }
        }
      }

      const batchName = currentBatch?.testBatchName;

      // 2. Fetch all tests associated with this batch name
      if (batchName) {
        const testsRes = await getWithTokenAPI(
          token,
          `/batch/get-all?Institution_id=${instId}&testBatchName=${encodeURIComponent(batchName)}&all=true`
        );

        if (testsRes?.statusCode === 200) {
          const rawTests = testsRes.data?.data || [];
          // Deduplicate by testNo and sort
          const uniqueTests = [
            ...new Map(rawTests.map((t) => [t.testNo, t])).values(),
          ].sort((a, b) => (Number(a.testNo) || 0) - (Number(b.testNo) || 0));

          setTests(uniqueTests);
        }
      }

      // 3. Fetch all students & payment subscriptions for this batch
      // Query both auto-approved (full payments) and approved/pending platform subscriptions
      const autoQuery = `${students_list_autoapproved}&institutionId=${instId}&limit=1000${
        batchName ? `&testBatchName=${encodeURIComponent(batchName)}` : `&batchId=${batchId}`
      }`;
      const approvedQuery = `${students_list_approved}&institutionId=${instId}&limit=1000${
        batchName ? `&testBatchName=${encodeURIComponent(batchName)}` : `&batchId=${batchId}`
      }`;
      const pendingQuery = `/admin/user-subscriptions?isApproved=false&paymentType=PLATFORM_ONLY&institutionId=${instId}&limit=1000${
        batchName ? `&testBatchName=${encodeURIComponent(batchName)}` : `&batchId=${batchId}`
      }`;

      const [resAuto, resApproved, resPending] = await Promise.all([
        getWithTokenAPI(token, autoQuery),
        getWithTokenAPI(token, approvedQuery),
        getWithTokenAPI(token, pendingQuery),
      ]);

      const listAuto = Array.isArray(resAuto?.data) ? resAuto.data : [];
      const listApproved = Array.isArray(resApproved?.data) ? resApproved.data : [];
      const listPending = Array.isArray(resPending?.data) ? resPending.data : [];

      const combined = [...listAuto, ...listApproved, ...listPending];

      // Format enrolled students
      const formatted = combined.map((sub) => {
        const studentFee = Number(sub.testPrice) || Number(currentBatch?.testFees) || 0;
        const isPaid = sub.paymentStatus === "paid" || sub.paymentType === "FULL" || sub.isApproved;
        const paidAmount = isPaid ? studentFee : 0;
        const dueBalance = isPaid ? 0 : studentFee;

        return {
          id: sub._id,
          studentId: sub.userId?._id,
          name: sub.userId?.name || sub.name || "N/A",
          phone: sub.userId?.phoneNumber || sub.phoneNumer || "N/A",
          city: sub.userId?.city || "N/A",
          state: sub.userId?.state || "",
          instituteId: sub.institutionId?._id || instId,
          batchId: sub.batchId?._id || batchId,
          totalFee: studentFee,
          paidAmount: paidAmount,
          dueBalance: dueBalance,
          paymentStatus: sub.paymentStatus || (isPaid ? "paid" : "pending"),
          paymentMethod: sub.paymentMethod || (sub.paymentType === "FULL" ? "RAZORPAY" : "OFFLINE / UPI"),
          transactionId: sub.transactionId || "N/A",
          receiptUrl: sub.upload_img || null,
          enrolledDate: sub.createdAt
            ? new Date(sub.createdAt).toLocaleDateString("en-GB")
            : "N/A",
          isApproved: sub.isApproved,
        };
      });

      setSubscriptions(formatted);
    } catch (err) {
      console.error("Error fetching batch details:", err);
      SafeToast.error("Failed to load batch details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBatchDetails();
  }, [batchId, targetInstitutionId]);

  // KPI Calculations
  const kpis = useMemo(() => {
    const totalFeesPerStudent = Number(batchInfo?.testFees) || 0;
    const totalStudents = subscriptions.length;
    const totalCollected = subscriptions.reduce((acc, curr) => acc + curr.paidAmount, 0);
    const totalDue = subscriptions.reduce((acc, curr) => acc + curr.dueBalance, 0);
    const publishedTestsCount = tests.filter((t) => t.isPublished).length;

    return {
      totalFeesPerStudent,
      totalStudents,
      totalCollected,
      totalDue,
      totalTests: tests.length || batchInfo?.numberofTests || 0,
      publishedTestsCount,
    };
  }, [batchInfo, subscriptions, tests]);

  // Filtered Tests
  const filteredTests = useMemo(() => {
    if (!testSearch.trim()) return tests;
    return tests.filter(
      (t) =>
        t.testBatchName?.toLowerCase().includes(testSearch.toLowerCase()) ||
        String(t.testNo).includes(testSearch) ||
        t.subTopic?.toLowerCase().includes(testSearch.toLowerCase())
    );
  }, [tests, testSearch]);

  // Filtered Students
  const filteredStudents = useMemo(() => {
    if (!studentSearch.trim()) return subscriptions;
    return subscriptions.filter(
      (s) =>
        s.name.toLowerCase().includes(studentSearch.toLowerCase()) ||
        s.phone.includes(studentSearch) ||
        s.paymentStatus.toLowerCase().includes(studentSearch.toLowerCase()) ||
        s.transactionId.toLowerCase().includes(studentSearch.toLowerCase())
    );
  }, [subscriptions, studentSearch]);

  return (
    <div className="batch-details-page">
      {/* Header Bar */}
      <div className="batch-details-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <button
            className="batch-back-btn"
            onClick={() =>
              navigate(
                queryInstId
                  ? `/batches?institutionId=${queryInstId}`
                  : "/batches"
              )
            }
          >
            <FiArrowLeft size={16} />
            Back to Batches
          </button>
          <div>
            <h2 style={{ margin: 0, color: "#0f172a", fontSize: "22px", fontWeight: 700 }}>
              {batchInfo?.testBatchName || "Batch Details"}
            </h2>
            <span style={{ fontSize: "13px", color: "#64748b" }}>
              Exam Type: <strong>{batchInfo?.examType?.value || "N/A"}</strong>
              {batchInfo?.headlineTagline ? ` • ${batchInfo.headlineTagline}` : ""}
            </span>
          </div>
        </div>
        <button
          type="button"
          className="delete-batch-btn"
          onClick={() => setShowDeleteBatchModal(true)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            padding: "10px 18px",
            borderRadius: "8px",
            border: "1px solid #fecaca",
            background: "#fef2f2",
            color: "#dc2626",
            fontWeight: "600",
            fontSize: "14px",
            cursor: "pointer",
            transition: "all 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#fee2e2";
            e.currentTarget.style.borderColor = "#f87171";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "#fef2f2";
            e.currentTarget.style.borderColor = "#fecaca";
          }}
        >
          <FiTrash2 size={16} />
          Delete Test Batch
        </button>
      </div>

      {/* KPI Cards Grid */}
      <div className="batch-kpi-grid">
        <div className="batch-kpi-card">
          <div className="batch-kpi-icon-wrap" style={{ background: "#eff6ff", color: "#2563eb" }}>
            <FiBookOpen size={22} />
          </div>
          <div className="batch-kpi-content">
            <span className="batch-kpi-label">Batch Fee / Student</span>
            <span className="batch-kpi-value">₹{kpis.totalFeesPerStudent}</span>
            <span className="batch-kpi-subtext">Per enrolled candidate</span>
          </div>
        </div>

        <div className="batch-kpi-card">
          <div className="batch-kpi-icon-wrap" style={{ background: "#f0fdf4", color: "#16a34a" }}>
            <FiUsers size={22} />
          </div>
          <div className="batch-kpi-content">
            <span className="batch-kpi-label">Total Enrolled</span>
            <span className="batch-kpi-value">{kpis.totalStudents}</span>
            <span className="batch-kpi-subtext">Active student subscriptions</span>
          </div>
        </div>

        <div className="batch-kpi-card">
          <div className="batch-kpi-icon-wrap" style={{ background: "#ecfdf5", color: "#059669" }}>
            <FiDollarSign size={22} />
          </div>
          <div className="batch-kpi-content">
            <span className="batch-kpi-label">Total Collected</span>
            <span className="batch-kpi-value" style={{ color: "#059669" }}>
              ₹{kpis.totalCollected}
            </span>
            <span className="batch-kpi-subtext">Confirmed payments</span>
          </div>
        </div>

        <div className="batch-kpi-card">
          <div className="batch-kpi-icon-wrap" style={{ background: "#fffbeb", color: "#d97706" }}>
            <FiClock size={22} />
          </div>
          <div className="batch-kpi-content">
            <span className="batch-kpi-label">Due / Pending Balance</span>
            <span className="batch-kpi-value" style={{ color: "#d97706" }}>
              ₹{kpis.totalDue}
            </span>
            <span className="batch-kpi-subtext">Awaiting clearance</span>
          </div>
        </div>

        <div className="batch-kpi-card">
          <div className="batch-kpi-icon-wrap" style={{ background: "#faf5ff", color: "#9333ea" }}>
            <FiFileText size={22} />
          </div>
          <div className="batch-kpi-content">
            <span className="batch-kpi-label">Assigned Tests</span>
            <span className="batch-kpi-value">
              {kpis.publishedTestsCount} / {kpis.totalTests}
            </span>
            <span className="batch-kpi-subtext">Published vs total tests</span>
          </div>
        </div>
      </div>

      {/* Tabs navigation */}
      <div className="batch-tabs-bar">
        <button
          className={`batch-tab-btn ${activeTab === "tests" ? "active" : ""}`}
          onClick={() => setActiveTab("tests")}
        >
          <FiFileText size={16} />
          Assigned Tests
          <span className="batch-tab-badge">{tests.length}</span>
        </button>

        <button
          className={`batch-tab-btn ${activeTab === "students" ? "active" : ""}`}
          onClick={() => setActiveTab("students")}
        >
          <FiUsers size={16} />
          Students & Fee Payments
          <span className="batch-tab-badge">{subscriptions.length}</span>
        </button>
      </div>

      {/* Tab 1: Assigned Tests Section */}
      {activeTab === "tests" && (
        <div className="batch-tab-content-card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", flexWrap: "wrap", gap: "12px" }}>
            <h3 style={{ margin: 0, fontSize: "16px", color: "#1e293b", fontWeight: 600 }}>
              Tests Assigned to this Batch
            </h3>
            <div style={{ position: "relative", minWidth: "260px" }}>
              <input
                type="text"
                placeholder="Search tests..."
                value={testSearch}
                onChange={(e) => setTestSearch(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px 12px 8px 34px",
                  borderRadius: "8px",
                  border: "1px solid #cbd5e1",
                  fontSize: "13px",
                  outline: "none",
                }}
              />
              <FiSearch
                size={16}
                style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }}
              />
            </div>
          </div>

          {loading ? (
            <TableSkeleton
              columns={[
                { label: "Test No" },
                { label: "Topic" },
                { label: "Questions" },
                { label: "Status" },
                { label: "Materials" },
                { label: "Actions" },
              ]}
              rows={4}
            />
          ) : filteredTests.length > 0 ? (
            <div className="batch-table-container">
              <table className="batch-custom-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Test No</th>
                    <th>Topic / Syllabus</th>
                    <th>Questions</th>
                    <th>Schedule / Status</th>
                    <th>Materials / Links</th>
                    <th style={{ textAlign: "right", paddingRight: "16px" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTests.map((test, idx) => {
                    const hasContent = Boolean(
                      (test.totalQuestions && parseInt(test.totalQuestions) > 0) ||
                      test.questionFiles?.questionPdf ||
                      test.questionFiles?.omrsheet ||
                      test.questionFiles?.videoLink
                    );
                    const isDraft = !test.isPublished && (test.isDraft === true || (test.isDraft === undefined && hasContent));

                    return (
                      <tr key={test._id || idx}>
                        <td>{idx + 1}</td>
                        <td>
                          <strong>Test {test.testNo || idx + 1}</strong>
                        </td>
                        <td>
                          <div style={{ fontWeight: 500, color: "#1e293b" }}>
                            {test.subTopic || test.headlineTagline || "General Test"}
                          </div>
                          {test.syllabus && (
                            <div style={{ fontSize: "12px", color: "#64748b" }}>{test.syllabus}</div>
                          )}
                        </td>
                        <td>
                          <span style={{ fontWeight: 600, color: "#0284c7" }}>
                            {test.totalQuestions || 0} Qs
                          </span>
                        </td>
                        <td>
                          <span
                            className={`badge-status ${
                              test.isPublished ? "published" : isDraft ? "draft" : "pending"
                            }`}
                          >
                            {test.isPublished ? "● Published" : isDraft ? "● Draft" : "● Not Started"}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                            {test.questionFiles?.questionPdf && (
                              <a
                                href={test.questionFiles.questionPdf}
                                target="_blank"
                                rel="noreferrer"
                                className="batch-action-link-btn"
                                title="View Question Paper"
                              >
                                <FiFileText size={12} /> Paper
                              </a>
                            )}
                            {test.questionFiles?.videoLink && (
                              <a
                                href={test.questionFiles.videoLink}
                                target="_blank"
                                rel="noreferrer"
                                className="batch-action-link-btn"
                                title="Watch Video Solution"
                              >
                                <FiExternalLink size={12} /> Video
                              </a>
                            )}
                            {test.questionFiles?.omrsheet && (
                              <a
                                href={test.questionFiles.omrsheet}
                                target="_blank"
                                rel="noreferrer"
                                className="batch-action-link-btn"
                                title="OMR Sheet"
                              >
                                OMR
                              </a>
                            )}
                          </div>
                        </td>
                        <td style={{ textAlign: "right", paddingRight: "16px" }}>
                          <div
                            style={{
                              display: "inline-flex",
                              gap: "8px",
                              alignItems: "center",
                              justifyContent: "flex-end",
                            }}
                          >
                            {/* Publish button (enabled only for tests in Draft status) */}
                            <button
                              type="button"
                              className="batch-action-publish-btn"
                              disabled={!isDraft || publishingTestId === test._id}
                              title={
                                test.isPublished
                                  ? "Already published"
                                  : isDraft
                                  ? "Click to publish this draft test"
                                  : "Add questions before publishing"
                              }
                              onClick={() => handlePublishTest(test)}
                            >
                              <FiCheckCircle size={13} />
                              {publishingTestId === test._id
                                ? "Publishing..."
                                : test.isPublished
                                ? "Published"
                                : "Publish"}
                            </button>

                            {/* Edit icon/button: redirects directly to selected test under Upload > Exams */}
                            <button
                              type="button"
                              className="batch-action-edit-btn"
                              title="Edit test under Upload > Exams"
                              onClick={() => handleEditTest(test)}
                            >
                              <FiEdit2 size={13} />
                              Edit
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="batch-empty-state">
              <FiFileText className="batch-empty-icon" />
              <h4>No Tests Found</h4>
              <p>No tests have been created or assigned under this batch yet.</p>
            </div>
          )}
        </div>
      )}

      {/* Tab 2: Students & Payments Section */}
      {activeTab === "students" && (
        <div className="batch-tab-content-card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", flexWrap: "wrap", gap: "12px" }}>
            <h3 style={{ margin: 0, fontSize: "16px", color: "#1e293b", fontWeight: 600 }}>
              Enrolled Students & Fee Collections
            </h3>
            <div style={{ position: "relative", minWidth: "260px" }}>
              <input
                type="text"
                placeholder="Search student or phone..."
                value={studentSearch}
                onChange={(e) => setStudentSearch(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px 12px 8px 34px",
                  borderRadius: "8px",
                  border: "1px solid #cbd5e1",
                  fontSize: "13px",
                  outline: "none",
                }}
              />
              <FiSearch
                size={16}
                style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }}
              />
            </div>
          </div>

          {loading ? (
            <TableSkeleton
              columns={[
                { label: "Student Name" },
                { label: "Contact Info" },
                { label: "Total Fee" },
                { label: "Paid" },
                { label: "Due Balance" },
                { label: "Payment Status" },
                { label: "Actions" },
              ]}
              rows={4}
            />
          ) : filteredStudents.length > 0 ? (
            <div className="batch-table-container">
              <table className="batch-custom-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Student Name</th>
                    <th>Contact Info</th>
                    <th>Total Fee</th>
                    <th>Paid Amount</th>
                    <th>Due Balance</th>
                    <th>Payment Status</th>
                    <th>Method / Trx ID</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStudents.map((std, idx) => (
                    <tr key={std.id || idx}>
                      <td>{idx + 1}</td>
                      <td>
                        <span
                          style={{
                            color: "#2563eb",
                            fontWeight: 600,
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            if (std.studentId) {
                              navigate(
                                `/students/profile/student/${std.studentId}/institute/${std.instituteId}/batchId/${std.batchId}`
                              );
                            }
                          }}
                        >
                          {std.name}
                        </span>
                        {std.city && (
                          <div style={{ fontSize: "11px", color: "#64748b" }}>{std.city}</div>
                        )}
                      </td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{std.phone}</div>
                        <div style={{ fontSize: "11px", color: "#94a3b8" }}>{std.enrolledDate}</div>
                      </td>
                      <td>
                        <strong>₹{std.totalFee}</strong>
                      </td>
                      <td>
                        <strong style={{ color: "#16a34a" }}>₹{std.paidAmount}</strong>
                      </td>
                      <td>
                        <strong style={{ color: std.dueBalance > 0 ? "#dc2626" : "#16a34a" }}>
                          ₹{std.dueBalance}
                        </strong>
                      </td>
                      <td>
                        <span
                          className={`badge-status ${
                            std.paymentStatus === "paid"
                              ? "paid"
                              : std.paymentStatus === "failed"
                              ? "failed"
                              : "pending"
                          }`}
                        >
                          ● {std.paymentStatus}
                        </span>
                      </td>
                      <td>
                        <div style={{ fontSize: "12px", fontWeight: 500 }}>{std.paymentMethod}</div>
                        <div style={{ fontSize: "11px", color: "#64748b", fontFamily: "monospace" }}>
                          {std.transactionId !== "N/A" ? std.transactionId : ""}
                        </div>
                      </td>
                      <td>
                        <div style={{ display: "flex", gap: "8px" }}>
                          {std.receiptUrl && (
                            <button
                              className="batch-action-receipt-btn"
                              onClick={() => setSelectedReceipt(std.receiptUrl)}
                              title="View Payment Receipt / Verification Proof"
                            >
                              <FiEye size={12} /> Receipt
                            </button>
                          )}
                          {std.studentId && (
                            <button
                              className="batch-action-link-btn"
                              onClick={() =>
                                navigate(
                                  `/students/profile/student/${std.studentId}/institute/${std.instituteId}/batchId/${std.batchId}`
                                )
                              }
                            >
                              Profile
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="batch-empty-state">
              <FiUsers className="batch-empty-icon" />
              <h4>No Students Enrolled</h4>
              <p>No candidates have registered or subscribed for this batch yet.</p>
            </div>
          )}
        </div>
      )}

      {/* Payment Receipt Modal */}
      {selectedReceipt && (
        <div
          className="batch-receipt-modal-backdrop"
          onClick={() => setSelectedReceipt(null)}
        >
          <div
            className="batch-receipt-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="batch-receipt-modal-header">
              <h3>Payment Proof / Receipt</h3>
              <button onClick={() => setSelectedReceipt(null)}>✕</button>
            </div>
            <div className="batch-receipt-modal-body">
              <img
                src={selectedReceipt}
                alt="Receipt"
                className="batch-receipt-img"
              />
            </div>
          </div>
        </div>
      )}

      {/* Delete Test Batch Confirmation Dialog */}
      {showDeleteBatchModal && (
        <div
          className="modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget && !isDeletingBatch) {
              setShowDeleteBatchModal(false);
            }
          }}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(15, 23, 42, 0.5)",
            backdropFilter: "blur(4px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 99999,
            padding: "20px",
          }}
        >
          <div
            style={{
              background: "#ffffff",
              borderRadius: "16px",
              width: "100%",
              maxWidth: "500px",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              overflow: "hidden",
              border: "1px solid #f1f5f9",
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "20px 24px 16px",
                borderBottom: "1px solid #f1f5f9",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <div
                  style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "50%",
                    background: "#fee2e2",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#dc2626",
                  }}
                >
                  <FiTrash2 size={20} />
                </div>
                <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "600", color: "#0f172a" }}>
                  Delete Test Batch
                </h3>
              </div>
              <button
                type="button"
                onClick={() => !isDeletingBatch && setShowDeleteBatchModal(false)}
                disabled={isDeletingBatch}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: isDeletingBatch ? "not-allowed" : "pointer",
                  color: "#94a3b8",
                  padding: "4px",
                  borderRadius: "6px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FiX size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: "20px 24px" }}>
              <p
                style={{
                  margin: "0 0 12px",
                  fontSize: "15px",
                  fontWeight: "600",
                  color: "#1e293b",
                  lineHeight: "1.5",
                }}
              >
                Are you sure you want to delete this test batch and its associated tests permanently?
              </p>
              <div
                style={{
                  padding: "12px 14px",
                  background: "#fff1f2",
                  borderRadius: "8px",
                  border: "1px solid #ffe4e6",
                  fontSize: "13px",
                  color: "#be123c",
                  lineHeight: "1.5",
                }}
              >
                Batch: <strong>{batchInfo?.testBatchName}</strong>
                <br />
                This action cannot be undone. All tests ({tests.length}), question papers, answer keys, and schedules in this batch will be permanently removed.
              </div>
            </div>

            {/* Modal Footer */}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
                padding: "16px 24px 20px",
                borderTop: "1px solid #f1f5f9",
                background: "#f8fafc",
              }}
            >
              <button
                type="button"
                disabled={isDeletingBatch}
                onClick={() => setShowDeleteBatchModal(false)}
                style={{
                  padding: "10px 18px",
                  borderRadius: "8px",
                  border: "1px solid #cbd5e1",
                  background: "#ffffff",
                  color: "#475569",
                  fontWeight: "500",
                  fontSize: "14px",
                  cursor: isDeletingBatch ? "not-allowed" : "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isDeletingBatch}
                onClick={handleDeleteBatch}
                style={{
                  padding: "10px 20px",
                  borderRadius: "8px",
                  border: "none",
                  background: isDeletingBatch ? "#fca5a5" : "#dc2626",
                  color: "#ffffff",
                  fontWeight: "600",
                  fontSize: "14px",
                  cursor: isDeletingBatch ? "not-allowed" : "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {isDeletingBatch ? "Deleting..." : "Delete Permanently"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchDetails;
